/** The "order", the basket: what's picked so far, each with its fields and toppings, and the way on to the review. */

import { useState } from "preact/hooks";
import type { ApiError } from "../../api/client.ts";
import type { OnCollision, TemplateOut, TemplatePlan } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { facetsOf, fieldsOf, linkCount, type Pick, undecided } from "../../lib/catalog.ts";
import { useId } from "../../lib/uid.ts";
import { Button, RemoveButton } from "../../ui/Button.tsx";
import { EntityGlyph } from "../../ui/EntityGlyph.tsx";
import { Input, Radios } from "../../ui/form.tsx";
import { BasketIcon } from "../../ui/icons.tsx";

export function Order(p: {
  order: readonly Pick[];
  entities: readonly TemplateOut[];
  /** The last plan (the one before, while the order's is previewed again). */
  plan: TemplatePlan | undefined;
  /** Whether the review can open: the order as it is previewed, with nothing left to decide. */
  ready: boolean;
  error: ApiError | undefined;
  onRemove: (key: string) => void;
  /** What to do with a pick the space has already (decided here or on its card). */
  onCollision: (key: string, on: OnCollision) => void;
  /** Its own name, kept apart (empty: the catalog's). */
  onName: (key: string, name: string) => void;
  onClear: () => void;
  onReview: () => void;
}) {
  const waiting = undecided(p.plan);
  const heading = useId();
  return (
    // Taller than the window with many picks: the list scrolls, its heading and the way on staying in view.
    <section
      aria-labelledby={heading}
      class="flex flex-col rounded-lg border border-zinc-200 bg-white p-4 lg:max-h-[calc(100dvh-2rem)]"
    >
      <div class="mb-3 flex items-baseline justify-between gap-2">
        <h2 id={heading} class="flex items-center gap-2 font-medium text-zinc-900">
          <BasketIcon class="text-zinc-500" />
          {t("catalog.order")}
        </h2>
        {p.order.length > 0 ? (
          <button type="button" onClick={p.onClear} class="text-xs text-zinc-500 hover:text-zinc-900 hover:underline">
            {t("catalog.clear")}
          </button>
        ) : null}
      </div>
      {p.order.length === 0 ? (
        <p class="text-sm text-zinc-500">{t("catalog.orderEmpty")}</p>
      ) : (
        <ul class="-mx-2 flex flex-col gap-2 px-2 lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain">
          {p.order.map((o) => {
            const e = p.entities.find((x) => x.key === o.key);
            if (!e) {
              return null;
            }
            const toppings = facetsOf(e)
              .filter((f) => o.facets.includes(f.key))
              .map((f) => f.name);
            // The space has it already: decided here (with many picks, its card would be a long way off), marked
            // whatever was chosen.
            const collision = p.plan?.collisions.find((c) => c.key === o.key);
            return (
              <li
                key={o.key}
                class={`flex flex-col gap-2 text-sm ${collision ? "-mx-2 rounded-md bg-amber-50 px-2 py-1.5 text-amber-950" : ""}`}
              >
                <div class="flex items-start gap-2">
                  <EntityGlyph icon={e.icon} size={16} pad={2} />
                  <div class="min-w-0 flex-1">
                    <div class="text-zinc-900">{e.name}</div>
                    <div class="text-xs text-zinc-500">
                      {t("catalog.fieldCount", { count: fieldsOf(e, o.facets).length })}
                      {toppings.length > 0 ? ` · + ${toppings.join(", ")}` : ""}
                    </div>
                  </div>
                  <RemoveButton label={t("catalog.remove", { name: e.name })} onClick={() => p.onRemove(o.key)} />
                </div>
                {collision ? (
                  <div class="flex flex-col gap-1.5 pl-7 text-xs">
                    <span>{t("catalog.collision", { name: collision.name })}</span>
                    <Radios<OnCollision>
                      small={true}
                      label={t("catalog.collisionChoice", { name: e.name })}
                      value={o.onCollision}
                      onValue={(on) => p.onCollision(o.key, on)}
                      options={[
                        { value: "merge", label: t("catalog.merge") },
                        { value: "separate", label: t("catalog.separate") },
                      ]}
                    />
                    {o.onCollision === "separate" ? (
                      <NewName
                        planned={p.plan?.entities.find((x) => x.key === o.key)?.name ?? e.name}
                        label={t("catalog.newName", { name: e.name })}
                        onName={(n) => p.onName(o.key, n)}
                      />
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {p.plan && p.order.length > 0 ? (
        <p class="mt-3 border-t border-zinc-100 pt-3 text-xs text-zinc-500">
          {t("catalog.summary", {
            created: p.plan.entities.filter((e) => e.action === "create").length,
            merged: p.plan.entities.filter((e) => e.action === "merge").length,
            links: linkCount(p.plan),
          })}
        </p>
      ) : null}
      {waiting > 0 ? <p class="mt-2 text-xs text-amber-800">{t("catalog.decide", { count: waiting })}</p> : null}
      {p.error ? (
        <p role="alert" class="mt-2 text-xs text-red-700">
          {p.error.message}
        </p>
      ) : null}
      <Button variant="primary" class="mt-4 w-full" disabled={!p.ready} onClick={p.onReview}>
        {t("catalog.review")}
      </Button>
    </section>
  );
}

/** A kept-apart pick's name, editable, showing the planned one (the catalog's or the one typed, made distinct from
 * the space's) while not being edited; taken when it's done (not each key, which would preview the order again every
 * time). Emptied, it's the catalog's again. */
function NewName(p: { planned: string; label: string; onName: (name: string) => void }) {
  const [editing, setEditing] = useState<string | null>(null);
  const done = () => {
    if (editing === null) {
      return;
    }
    const v = editing.trim();
    setEditing(null);
    if (v === "") {
      p.onName("");
    } else if (v !== p.planned) {
      p.onName(v);
    }
  };
  return (
    <div class="pl-5">
      <Input
        compact={true}
        class="w-full"
        aria-label={p.label}
        value={editing ?? p.planned}
        onValue={setEditing}
        onBlur={done}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            done();
          }
        }}
      />
    </div>
  );
}
